import React, { useEffect, useState, useRef } from "react";

// --- Helper: Format Date ---
const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

// --- Helper: Get Auth Headers ---
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LidarCesium = ({ viewer, latestSurvey, apiBase }) => {
  const [layers, setLayers] = useState([]);

  // refs like VectorCesium
  const loadedTilesetsRef = useRef({}); // dsId -> tileset
  const loadingRef = useRef({});        // dsId -> true while loading
  const isMountedRef = useRef(false);
  const hasZoomedRef = useRef(false);

  // --- 1. Discovery Phase ---
  useEffect(() => {
    if (!viewer || !latestSurvey) return;
    isMountedRef.current = true;

    const discoverLayers = async () => {
      try {
        const loc = String(latestSurvey.location_name || latestSurvey.location || latestSurvey.name || "").trim();
        const sv = toDateKey(latestSurvey.survey_time || latestSurvey.survey_date || latestSurvey.created_at);
        const org = localStorage.getItem("organization") || "";

        if (!loc || !sv) return;

        const params = new URLSearchParams();
        params.append("location", loc);
        params.append("survey_time", sv);
        if (org) params.append("organization", org);

        const discoveryUrl = `${apiBase}/lidar/tiles-discovery?${params.toString()}`;
        const r = await fetch(discoveryUrl, { headers: { "Accept": "application/json", ...getAuthHeaders() } });
        if (!r.ok) return;

        const foundLayers = await r.json();
        const candidates = Array.isArray(foundLayers) ? foundLayers : [];

        if (candidates.length === 0) {
          if (isMountedRef.current) setLayers([]);
          return;
        }

        const resolved = candidates.map((cand) => {
          const layerSlug = cand.slug || cand.name || "default";
          const uniqueId = `lidar-${loc}-${sv}-${layerSlug}`;
          const url = `${apiBase}/lidar/${encodeURIComponent(loc)}/${encodeURIComponent(sv)}/${encodeURIComponent(layerSlug)}/tiles/tileset.json`;
          return { id: uniqueId, name: layerSlug, url: url, visible: true };
        });

        if (isMountedRef.current) {
            setLayers(resolved);
            window.dispatchEvent(new CustomEvent("lidar-layers-discovered", {
                detail: { location: loc, survey_time: sv, layers: resolved }
            }));
        }
      } catch (err) {
        console.warn("[LidarCesium] Discovery Error:", err);
      }
    };

    discoverLayers();
    return () => { isMountedRef.current = false; };
  }, [latestSurvey, apiBase, viewer]);

  // --- 2. Load Tileset with Height Correction ---
  const loadTileset = async (layer) => {
    if (!viewer || !layer) return null;
    const Cesium = window.Cesium;
    if (!Cesium) return null;

    const dsId = layer.id;
    if (loadingRef.current[dsId]) return null; // already loading

    loadingRef.current[dsId] = true;
    try {
      // Setup Lighting to make points visible
      const shArray = [];
      for (let i = 0; i < 9; i++) shArray.push(new Cesium.Cartesian3(0.0, 0.0, 0.0));
      const lightingFix = new Cesium.ImageBasedLighting({
        imageBasedLightingFactor: new Cesium.Cartesian2(0.5, 0.5),
        sphericalHarmonicCoefficients: shArray
      });

      // Load Tileset
      const tileset = await Cesium.Cesium3DTileset.fromUrl(layer.url, {
        headers: getAuthHeaders(),
        maximumScreenSpaceError: 128,
        skipLevelOfDetail: true,
        enableCollision: false,
        imageBasedLighting: lightingFix
      });

      if (!isMountedRef.current || viewer.isDestroyed()) {
        if (tileset && !tileset.isDestroyed()) tileset.destroy();
        return null;
      }

      // Add to Scene
      viewer.scene.primitives.add(tileset);
      tileset.show = !!layer.visible;
      tileset.style = new Cesium.Cesium3DTileStyle({ pointSize: 4 });

      // Store Reference
      loadedTilesetsRef.current[dsId] = tileset;

      // --- THE FIX: Handle Height Clamping ---
      tileset.readyPromise.then((t) => {
        try {
          // 1. Get the center of the tileset data
          const centerCartesian = t.boundingSphere.center;
          const centerCartographic = Cesium.Cartographic.fromCartesian(centerCartesian);
          
          // 2. Determine if map is Flat (Ellipsoid)
          const isFlatMap = (viewer.terrainProvider instanceof Cesium.EllipsoidTerrainProvider);

          if (isFlatMap) {
            // 3. Calculate position at Height = 0 (Sea Level)
            const surfaceCartesian = Cesium.Cartesian3.fromRadians(
                centerCartographic.longitude,
                centerCartographic.latitude,
                0.0
            );

            // 4. Calculate the translation vector (Surface - Original Center)
            const translation = Cesium.Cartesian3.subtract(
                surfaceCartesian, 
                centerCartesian, 
                new Cesium.Cartesian3()
            );

            console.log(`[LidarCesium] Flat Map detected. Clamping ${layer.name} to 0. Shift: ${centerCartographic.height.toFixed(2)}m`);
            
            // 5. Apply the translation
            t.modelMatrix = Cesium.Matrix4.fromTranslation(translation);

          } else {
            // If Terrain is active, use original absolute coordinates
            console.log(`[LidarCesium] Terrain detected. Using absolute height for ${layer.name}.`);
            t.modelMatrix = Cesium.Matrix4.IDENTITY;
          }

          viewer.scene.requestRender();
        } catch (e) {
          console.warn("[LidarCesium] readyPromise compute failed", e);
        }
      }).catch(e => {
        console.warn("[LidarCesium] tileset readyPromise failed", e);
      });

      tileset.tileFailed.addEventListener((err) => {
        console.warn("[LidarCesium] Tile failed", err && err.url);
      });

      return tileset;
    } catch (err) {
      console.error("[LidarCesium] loadTileset error", err);
      return null;
    } finally {
      delete loadingRef.current[dsId];
    }
  };

  // --- 3. Clean up Tileset ---
  const destroyTileset = (dsId) => {
    try {
      const tileset = loadedTilesetsRef.current[dsId];
      if (!tileset) return;
      if (!tileset.isDestroyed()) {
        try { viewer.scene.primitives.remove(tileset); } catch (e) {}
        try { tileset.destroy(); } catch (e) {}
      }
    } catch (e) {
      console.warn("[LidarCesium] destroyTileset error", e);
    } finally {
      delete loadedTilesetsRef.current[dsId];
      delete loadingRef.current[dsId];
    }
  };

  // --- 4. Restart Logic (Crucial for Map Type Changes) ---
  const restartAllTilesets = async () => {
    if (!viewer || !Array.isArray(layers) || layers.length === 0) return;

    // Destroy existing
    Object.keys(loadedTilesetsRef.current).forEach(dsId => {
      destroyTileset(dsId);
    });

    // Small delay to allow Cesium to clear memory/references
    await new Promise(r => setTimeout(r, 120));

    // Reload sequentially
    for (const layer of layers) {
      if (!isMountedRef.current) break;
      if (!loadedTilesetsRef.current[layer.id] && !loadingRef.current[layer.id]) {
        await loadTileset(layer);
      }
    }

    // Zoom once to the first loaded tileset
    if (!hasZoomedRef.current && layers.length > 0) {
      const first = loadedTilesetsRef.current[layers[0].id];
      if (first) {
        try {
          await viewer.zoomTo(first);
          hasZoomedRef.current = true;
        } catch (_) {}
      }
    }
  };

  // --- 5. Initial Sync ---
  useEffect(() => {
    if (!viewer) return;
    const Cesium = window.Cesium;
    if (!Cesium) return;

    const sync = async () => {
      for (const layer of layers) {
        if (!isMountedRef.current) break;
        if (loadedTilesetsRef.current[layer.id] || loadingRef.current[layer.id]) continue;
        await loadTileset(layer);
      }
    };

    sync();
  }, [viewer, layers]);

  // --- 6. Event Listeners (Map Changes) ---
  useEffect(() => {
    if (!viewer) return;
    const Cesium = window.Cesium;
    if (!Cesium) return;

    const onMorphComplete = () => {
      console.log("[LidarCesium] morphComplete -> restartAllTilesets");
      restartAllTilesets();
    };

    // This fires when switching from Flat to Terrain or vice versa
    const onTerrainProviderChanged = () => {
      console.log("[LidarCesium] terrainProviderChanged -> restartAllTilesets");
      restartAllTilesets();
    };

    const onResize = () => {
      restartAllTilesets();
    };

    const onCustomRestart = (e) => {
      const id = e && e.detail && e.detail.id;
      if (id) {
        destroyTileset(id);
        setTimeout(() => {
          const layer = layers.find(l => l.id === id);
          if (layer) loadTileset(layer);
        }, 120);
      } else {
        restartAllTilesets();
      }
    };

    try { viewer.scene.morphComplete.addEventListener(onMorphComplete); } catch (e) {}
    try { viewer.terrainProviderChanged.addEventListener(onTerrainProviderChanged); } catch (e) {}
    window.addEventListener("resize", onResize);
    window.addEventListener("lidar-restart", onCustomRestart);

    return () => {
      try { viewer.scene.morphComplete.removeEventListener && viewer.scene.morphComplete.removeEventListener(onMorphComplete); } catch (e) {}
      try { viewer.terrainProviderChanged.removeEventListener && viewer.terrainProviderChanged.removeEventListener(onTerrainProviderChanged); } catch (e) {}
      window.removeEventListener("resize", onResize);
      window.removeEventListener("lidar-restart", onCustomRestart);
    };
  }, [viewer, layers]);

  // --- 7. Toggle Visibility ---
  useEffect(() => {
    const handleToggle = (e) => {
      const { id, visible } = e.detail || {};
      if (id && id.startsWith("lidar-")) {
        const tileset = loadedTilesetsRef.current[id];
        if (tileset && !tileset.isDestroyed()) {
          tileset.show = !!visible;
          viewer.scene.requestRender();
        }
        setLayers(prev => prev.map(l => l.id === id ? { ...l, visible: !!visible } : l));
      }
    };
    window.addEventListener("map-toggle-layer", handleToggle);
    return () => window.removeEventListener("map-toggle-layer", handleToggle);
  }, [viewer]);

  // --- 8. Cleanup ---
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (viewer && !viewer.isDestroyed()) {
        Object.keys(loadedTilesetsRef.current).forEach(dsId => {
          destroyTileset(dsId);
        });
      }
      loadedTilesetsRef.current = {};
      loadingRef.current = {};
      hasZoomedRef.current = false;
    };
  }, [viewer]);

  return null;
};

export default LidarCesium;